// Tiny isomorphic AT Protocol client. No SDK — just fetch + JSON.

import { APPVIEW, PLC_DIRECTORY } from '../config.js';

// A single request must never wedge the home-feed refresh loop. Attach a
// 15s abort so a hung fetch rejects instead of hanging forever. Feature-
// detected: environments without `AbortSignal.timeout` (older runtimes)
// simply run with no signal.
//
// A caller's own signal (a cancel button, an unmount) is kept, but it is not a
// substitute: it fires when the caller gives up, and nothing gives up on a
// server that has simply stopped answering. So the two are combined where the
// runtime can (`AbortSignal.any`); where it can't, the caller's signal stands
// alone, as it always did.
const REQUEST_TIMEOUT_MS = 15_000;
function withTimeout(init, ms = REQUEST_TIMEOUT_MS) {
  const base = init || {};
  if (
    typeof AbortSignal === 'undefined' ||
    typeof AbortSignal.timeout !== 'function'
  ) {
    return base;
  }
  const timeout = AbortSignal.timeout(ms);
  if (!base.signal) return { ...base, signal: timeout };
  if (typeof AbortSignal.any !== 'function') return base;
  const signal = AbortSignal.any([base.signal, timeout]);
  // The combined signal holds its sources weakly, and Node 20 lets a pending
  // `AbortSignal.timeout` be collected when nothing else refers to it: after
  // a GC the timeout never fired, and the request hung as if it had none.
  // Hanging it off the signal the request keeps keeps it alive as long as
  // the request is.
  signal.timeoutSignal = timeout;
  return { ...base, signal };
}

/**
 * `fetch` with the same 15s ceiling every request in this module carries. For
 * the clients that talk to other services (the AppView batch reads in
 * subjectResolver.js, Slingshot, Constellation) and so can't go through
 * `fetchJson`, but must not be able to hang the page that waits on them either.
 */
export function fetchWithTimeout(url, init, ms) {
  return fetch(url, withTimeout(init, ms));
}

/**
 * Did this request fail because the record isn't there, rather than because
 * the server couldn't be asked? A PDS answers a missing record with a 400
 * `RecordNotFound` (and some proxies with a 404); fetchJson's errors carry the
 * status and the body's text, an @atproto/api one the XRPC code as `error`.
 * Anything else — a timeout, a 5xx, a refused connection — says nothing about
 * the record, and a caller that remembers answers must not remember it as gone.
 *
 * Any 400 counts, since getRecord also answers an rkey it won't parse with
 * one: for a reader that is the same as no record. The site's two writers
 * want narrower or broader answers and have their own, named for it:
 * guestbook.js's isXrpcRecordNotFound and recordWrites.js's isRecordMissing.
 */
export function isRecordNotFound(err) {
  const status = err?.status;
  return (
    status === 400 ||
    status === 404 ||
    err?.error === 'RecordNotFound' ||
    /RecordNotFound|Could not locate record/i.test(err?.message || '')
  );
}

async function fetchJson(url, init) {
  const res = await fetch(url, withTimeout(init));
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const err = new Error(`HTTP ${res.status} ${res.statusText} for ${url} :: ${text.slice(0, 200)}`);
    err.status = res.status;
    err.url = url;
    throw err;
  }
  return res.json();
}

/**
 * Resolve a DID's PDS endpoint from its DID document — the PLC directory for
 * `did:plc:*`, the host's `/.well-known/did.json` for `did:web:*`.
 * Returns a URL string with no trailing slash.
 */
export async function resolvePds(did) {
  const doc = await getDidDocument(did);
  const services = doc?.service || [];
  // Atproto PDS service id can be `#atproto_pds`.
  const pds =
    services.find((s) => s.id === '#atproto_pds')?.serviceEndpoint ||
    services[0]?.serviceEndpoint;
  if (!pds) throw new Error(`No PDS endpoint for ${did}`);
  return pds.replace(/\/$/, '');
}

// ---------------------------------------------------------------------------
// DID documents and the PLC audit log, cached in memory.
//
// Cached as the PROMISE, not the answer. A page load fires a dozen
// resolvePds(ME_DID) calls in the same tick (the chrome bar, the theme, the
// feed, every record hook), and caching only the settled document let all of
// them past the cache before the first one came back: ten identical
// plc.directory requests per load, which the browser then queued one behind
// another on its HTTP cache lock. A lookup that fails is dropped, so the next
// caller asks again rather than inheriting the failure.
//
// Ten minutes for the document because what callers read off it — the PDS,
// the handle — moves only on an account migration, which is rare and
// deliberate. The audit log keeps 30s: the explorer view it feeds exists to
// show what just changed, and nothing polls it.
const DID_DOC_TTL = 10 * 60_000;
const PLC_AUDIT_TTL = 30_000;
const _didDocCache = new Map(); // did -> { promise, ts }
const _plcAuditCache = new Map(); // did -> { promise, ts }

function cachedRead(cache, key, ttl, read) {
  const held = cache.get(key);
  if (held && Date.now() - held.ts < ttl) return held.promise;
  const promise = read();
  cache.set(key, { promise, ts: Date.now() });
  promise.catch(() => {
    if (cache.get(key)?.promise === promise) cache.delete(key);
  });
  return promise;
}

/**
 * Fetch the DID document from the PLC directory. `did:plc:*` only; use
 * `getDidDocument` for a DID of either method.
 */
export async function getPlcDocument(did) {
  if (!did) throw new Error('getPlcDocument: missing did');
  return cachedRead(_didDocCache, did, DID_DOC_TTL, () =>
    fetchJson(`${PLC_DIRECTORY}/${encodeURIComponent(did)}`),
  );
}

/**
 * Where a `did:web` keeps its document: `did:web:example.com` →
 * `https://example.com/.well-known/did.json`. A port arrives percent-encoded
 * (`did:web:localhost%3A2583`). Further `:` segments would be a path, which
 * atproto doesn't use for accounts, so those are refused rather than guessed
 * at. `null` for anything that isn't a usable `did:web`.
 */
export function didWebDocumentUrl(did) {
  const m = /^did:web:([^:/?#]+)$/.exec(String(did || ''));
  if (!m) return null;
  const host = m[1].replace(/%3A/gi, ':');
  if (!/^[a-z0-9.-]+(:\d+)?$/i.test(host)) return null;
  return `https://${host}/.well-known/did.json`;
}

/**
 * The DID document for a `did:plc` or a `did:web`, through the same cache.
 *
 * A `did:web` is read from its own host. Server-side that always works; from a
 * browser it also needs the host to allow cross-origin reads of the file, and
 * where it doesn't this fails much as it did before did:web was handled at
 * all — the explorer reports the repo unreachable, a subject shows as
 * unavailable.
 */
export async function getDidDocument(did) {
  if (!did) throw new Error('getDidDocument: missing did');
  if (!String(did).startsWith('did:web:')) return getPlcDocument(did);
  const url = didWebDocumentUrl(did);
  if (!url) throw new Error(`Unsupported did:web identifier ${did}`);
  return cachedRead(_didDocCache, did, DID_DOC_TTL, () => fetchJson(url));
}

/**
 * Fetch the PLC audit log for a `did:plc:*`. Returns an array of operations
 * (oldest → newest).
 */
export async function getPlcAuditLog(did) {
  if (!did) throw new Error('getPlcAuditLog: missing did');
  return cachedRead(_plcAuditCache, did, PLC_AUDIT_TTL, () =>
    fetchJson(`${PLC_DIRECTORY}/${encodeURIComponent(did)}/log/audit`),
  );
}

/**
 * AppView — `com.atproto.identity.resolveHandle`. Falls back to bsky.social
 * if the appview can't resolve the handle.
 */
export async function resolveHandle(handle) {
  const qs = `handle=${encodeURIComponent(handle)}`;
  try {
    const res = await fetchJson(`${APPVIEW}/xrpc/com.atproto.identity.resolveHandle?${qs}`);
    if (res?.did) return res.did;
  } catch {
    // fall through
  }
  const res = await fetchJson(`https://bsky.social/xrpc/com.atproto.identity.resolveHandle?${qs}`);
  return res?.did || null;
}

/**
 * Normalize a user-supplied identifier (handle, DID, or PDS URL) into
 * `{ did, handle, pds }`. Throws on failure.
 *
 *   - DID input: `resolvePds` + `describeRepo` for the canonical handle.
 *   - Handle input: `resolveHandle` first, then DID flow.
 */
export async function resolveIdentifier(input) {
  const trimmed = String(input || '').trim();
  if (!trimmed) throw new Error('resolveIdentifier: empty input');

  let did = null;
  if (trimmed.startsWith('did:')) {
    did = trimmed;
  } else {
    did = await resolveHandle(trimmed);
    if (!did) throw new Error(`Could not resolve handle ${trimmed}`);
  }

  const pds = await resolvePds(did);
  let handle = null;
  try {
    const desc = await describeRepo(pds, did);
    handle = desc?.handle || null;
  } catch {
    // describeRepo failures are non-fatal; the caller can still browse by DID.
  }
  return { did, handle, pds };
}

/**
 * PDS — `com.atproto.repo.describeRepo`. Returns
 * `{ handle, did, didDoc, collections, handleIsCorrect }`.
 */
export async function describeRepo(pds, repo) {
  const params = new URLSearchParams({ repo });
  return fetchJson(`${pds}/xrpc/com.atproto.repo.describeRepo?${params}`);
}

/**
 * PDS — `com.atproto.repo.listRecords` (single page). Returns
 * `{ records, cursor }`. Use this when the caller wants to control
 * pagination (e.g. the explorer's "Load more" button); the higher-level
 * `listRecords` below auto-paginates and is dangerous for huge collections.
 */
export async function listRecordsPage(
  pds,
  { repo, collection, limit = 50, cursor, reverse = false } = {},
) {
  const params = new URLSearchParams({ repo, collection, limit: String(limit) });
  if (reverse) params.set('reverse', 'true');
  if (cursor) params.set('cursor', cursor);
  return fetchJson(`${pds}/xrpc/com.atproto.repo.listRecords?${params}`);
}

/**
 * AppView — `app.bsky.actor.getProfile`.
 */
export async function getProfile(actor, { appview = APPVIEW, cache } = {}) {
  const url = `${appview}/xrpc/app.bsky.actor.getProfile?actor=${encodeURIComponent(actor)}`;
  // `cache: 'no-store'` lets callers that need a *current* value (e.g. the
  // hourly-changing avatar) bypass the browser HTTP cache.
  return fetchJson(url, cache ? { cache } : undefined);
}

/**
 * AppView — `app.bsky.actor.getProfiles`. Resolves many actors at once and
 * returns a `{ did: handle }` map. The endpoint takes 25 per call, so a long
 * list is chunked; a chunk that fails is skipped rather than failing the lot,
 * leaving those DIDs unresolved for the caller to label as it sees fit.
 */
export async function resolveHandles(dids, opts) {
  const profiles = await resolveProfiles(dids, opts);
  const out = {};
  for (const [did, p] of Object.entries(profiles)) if (p.handle) out[did] = p.handle;
  return out;
}

/**
 * The same call, keeping the parts a face needs: `{ did: { handle,
 * displayName, avatar, followers, follows } }`. `resolveHandles` is the thin
 * wrapper over this for the callers that only want names.
 *
 * A DID that resolves to nothing is simply absent — a deactivated account and
 * an account we failed to reach look the same from here, and neither should be
 * invented.
 *
 * The two counts are here for Ratioed, which stamps them onto an event log at
 * measurement time to size the audience each participant carried a piece to.
 * They are numbers or they are undefined: an account whose profile omits them
 * has not been measured, and recording that as zero would read as an account
 * nobody follows.
 */
export async function resolveProfiles(dids, { appview = APPVIEW } = {}) {
  const unique = Array.from(new Set((dids || []).filter(Boolean)));
  const out = {};
  for (let i = 0; i < unique.length; i += 25) {
    const params = new URLSearchParams();
    for (const did of unique.slice(i, i + 25)) params.append('actors', did);
    try {
      // Tried twice. A chunk is up to 25 accounts and a Ratioed measurement is
      // usually one chunk for the whole piece, so a single 500 or a dropped
      // connection is the difference between a log that names thirteen people
      // and a log that names none of them — which is what happened to take 16,
      // and what gets written onto a record that is supposed to be permanent.
      // Sequential and small: this is not a retry loop, it is a second ask.
      const res = await fetchJson(`${appview}/xrpc/app.bsky.actor.getProfiles?${params}`).catch(
        async (err) => {
          await new Promise((r) => setTimeout(r, 400));
          return fetchJson(`${appview}/xrpc/app.bsky.actor.getProfiles?${params}`).catch(() => {
            throw err;
          });
        },
      );
      for (const p of res?.profiles || []) {
        if (!p?.did) continue;
        out[p.did] = {
          handle: p.handle || '',
          displayName: p.displayName || '',
          avatar: p.avatar || '',
          ...(typeof p.followersCount === 'number' ? { followers: p.followersCount } : {}),
          ...(typeof p.followsCount === 'number' ? { follows: p.followsCount } : {}),
        };
      }
    } catch {
      /* leave this chunk unresolved */
    }
  }
  return out;
}

/**
 * AppView — `app.bsky.feed.getLikes`. Who has liked a post, newest first.
 *
 * The AppView rather than the backlink index because this is what the Ratioed
 * studio watches while a piece is up, and there the only thing that matters is
 * how fast a like is visible: the whole artwork is a race between one like and
 * the artist noticing it. The backlink index is the more complete reader and
 * takes the measurement afterwards, once it has caught up.
 */
export async function getLikes(uri, { appview = APPVIEW, limit = 25 } = {}) {
  const params = new URLSearchParams({ uri, limit: String(limit) });
  // No HTTP cache: a stale answer here is a piece left standing.
  return fetchJson(`${appview}/xrpc/app.bsky.feed.getLikes?${params}`, { cache: 'no-store' });
}

/**
 * AppView — `app.bsky.feed.getPosts`. Up to 25 posts by AT URI, keyed by URI.
 *
 * Exists for the CID, which is the one thing you cannot construct: liking or
 * replying to a post needs a strong ref, and a stream event carries a record
 * key and no hash. Also hands back the handle and the text, which is why a
 * caller with a bare DID gets a readable row out of the same round trip — and
 * the embed, which is the one thing a stream event cannot be made to yield at
 * all: the live feed learns that somebody replied with a picture only by
 * asking here (see components/RatioedTicker.jsx).
 *
 * A post written seconds ago may not be in the AppView yet — the stream is
 * faster than the index it beats, which is the entire point of the stream — so
 * a caller that must have the ref falls back to reading the record from the
 * author's PDS.
 */
export async function getPosts(uris, { appview = APPVIEW } = {}) {
  const list = Array.from(new Set((uris || []).filter(Boolean))).slice(0, 25);
  if (!list.length) return {};
  const params = new URLSearchParams();
  for (const uri of list) params.append('uris', uri);
  const res = await fetchJson(`${appview}/xrpc/app.bsky.feed.getPosts?${params}`, {
    cache: 'no-store',
  });
  const out = {};
  for (const p of res?.posts || []) {
    if (!p?.uri || !p?.cid) continue;
    out[p.uri] = {
      cid: p.cid,
      did: p.author?.did || '',
      handle: p.author?.handle || '',
      text: typeof p.record?.text === 'string' ? p.record.text : '',
      // The resolved `#view` form, with CDN URLs on it — `null` rather than
      // undefined so a hydrated post with no embed is distinguishable from one
      // that has not been asked about yet.
      embed: p.embed || null,
    };
  }
  return out;
}

/**
 * AppView — `app.bsky.feed.getPostThread`. Returns a thread view (parent
 * chain + replies) anchored at the given AT URI.
 */
export async function getPostThread(uri, { appview = APPVIEW, depth = 0, parentHeight = 6 } = {}) {
  const params = new URLSearchParams({
    uri,
    depth: String(depth),
    parentHeight: String(parentHeight),
  });
  return fetchJson(`${appview}/xrpc/app.bsky.feed.getPostThread?${params}`);
}

/**
 * AppView — `app.bsky.feed.getAuthorFeed`. Auto-paginates up to `max` items.
 */
export async function getAuthorFeed(actor, { appview = APPVIEW, limit = 100, max = 200 } = {}) {
  const items = [];
  let cursor = null;
  while (items.length < max) {
    const params = new URLSearchParams({ actor, limit: String(Math.min(limit, max - items.length)) });
    if (cursor) params.set('cursor', cursor);
    const res = await fetchJson(`${appview}/xrpc/app.bsky.feed.getAuthorFeed?${params}`);
    const batch = res?.feed || [];
    items.push(...batch);
    if (!res.cursor || batch.length === 0) break;
    cursor = res.cursor;
  }
  return items;
}

/**
 * PDS — `com.atproto.repo.listRecords`. Auto-paginates up to `max` records.
 * Returns the raw `records` array (each `{uri, cid, value}`).
 */
export async function listRecords(pds, { repo, collection, limit = 100, max = 500, reverse = false } = {}) {
  const records = [];
  let cursor = null;
  while (records.length < max) {
    const want = Math.min(limit, max - records.length);
    const params = new URLSearchParams({
      repo,
      collection,
      limit: String(want),
    });
    if (reverse) params.set('reverse', 'true');
    if (cursor) params.set('cursor', cursor);
    const res = await fetchJson(`${pds}/xrpc/com.atproto.repo.listRecords?${params}`);
    const batch = res?.records || [];
    records.push(...batch);
    // A short page is the last page. The PDS hands back a cursor with any page
    // that isn't empty, so following it after a short one always cost a round
    // trip for an empty answer — about seventeen of them per home-feed build,
    // each on the rebuild's critical path. The callers read the owner's own
    // repo on a reference PDS, which fills a page whenever there's more to fill
    // it with. (getAuthorFeed above keeps following: the AppView drops blocked
    // and deleted posts from a page after choosing it, so a short page there
    // says nothing.)
    if (!res?.cursor || batch.length < want) break;
    cursor = res.cursor;
  }
  return records;
}

/**
 * PDS — `com.atproto.repo.getRecord`.
 */
export async function getRecord(pds, { repo, collection, rkey }) {
  const params = new URLSearchParams({ repo, collection, rkey });
  return fetchJson(`${pds}/xrpc/com.atproto.repo.getRecord?${params}`);
}

/**
 * PDS — `com.atproto.sync.getLatestCommit`. Returns `{ cid, rev }` for the
 * repo's current head commit. The `rev` is a TID, so `tidToTimestamp(rev)`
 * recovers when the repo was last written to — a cheap "last active" signal
 * with no extra call to list records.
 */
export async function getLatestCommit(pds, did) {
  const params = new URLSearchParams({ did });
  return fetchJson(`${pds}/xrpc/com.atproto.sync.getLatestCommit?${params}`);
}

// The repo head, shared by everything that polls it on the same beat. The
// refresh tick (lib/refreshTick.js) fires the chrome bar's live readers and
// the home feed back to back, and each of them only wants to know whether
// anything has been written since it last looked — one answer serves them
// all. Short enough that the next tick, 30s on, always asks again.
const REV_SHARE_MS = 5_000;
const _revCache = new Map(); // did -> { promise, ts }

/**
 * The `rev` of a repo's head commit, or null when the PDS doesn't say.
 * Concurrent and near-simultaneous callers share one getLatestCommit.
 */
export function getRepoRev(did) {
  return cachedRead(_revCache, did, REV_SHARE_MS, async () => {
    const pds = await resolvePds(did);
    const commit = await getLatestCommit(pds, did);
    return commit?.rev || null;
  });
}

/**
 * Build an `at://` URI from parts.
 */
export function toAtUri({ did, collection, rkey }) {
  return `at://${did}/${collection}/${rkey}`;
}

/**
 * Extract the rkey from an `at://did/collection/rkey` URI.
 */
export function rkeyFromAtUri(atUri) {
  if (!atUri) return null;
  const m = String(atUri).match(/^at:\/\/[^/]+\/[^/]+\/([^/?#]+)/);
  return m ? m[1] : null;
}

// The base32-sortable alphabet atproto TIDs are encoded with.
const TID_ALPHABET = '234567abcdefghijklmnopqrstuvwxyz';

/**
 * Recover the timestamp embedded in a TID record key.
 *
 * A TID is a 13-char base32-sortable string whose bits are
 * `0 | 53-bit microseconds-since-epoch | 10-bit clock id`, so the key alone
 * pins when the record was minted. Useful when a record omits an explicit
 * `createdAt` (the legacy guestbook signatures often do) — the rkey is then
 * the only timestamp available. Returns an ISO string, or `null` when `rkey`
 * isn't a well-formed TID (custom / human-chosen rkeys aren't).
 */
export function tidToTimestamp(rkey) {
  const s = String(rkey || '');
  if (s.length !== 13) return null;
  let n = 0n;
  for (const ch of s) {
    const i = TID_ALPHABET.indexOf(ch);
    if (i < 0) return null; // not a TID — bail rather than decode garbage
    n = n * 32n + BigInt(i);
  }
  const ms = Number((n >> 10n) / 1000n); // drop the clock id, µs → ms
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Convert an `at://` URI (or a bare DID) into the corresponding
 * `/exploring/...` SPA path. Returns `null` for empty/unparseable input.
 *
 *   at://did:plc:abc/app.bsky.feed.post/xyz  → /exploring/did:plc:abc/app.bsky.feed.post/xyz
 *   at://did:plc:abc/app.bsky.feed.post      → /exploring/did:plc:abc/app.bsky.feed.post
 *   at://did:plc:abc                         → /exploring/did:plc:abc
 *   did:plc:abc                              → /exploring/did:plc:abc
 */
export function explorerPathFromAtUri(input) {
  if (!input) return null;
  const s = String(input);
  if (s.startsWith('did:')) return `/exploring/${s}`;
  const m = s.match(/^at:\/\/([^/]+)(?:\/([^/?#]+)(?:\/([^/?#]+))?)?/);
  if (!m) return null;
  const [, repo, collection, rkey] = m;
  if (rkey) return `/exploring/${repo}/${collection}/${encodeURIComponent(rkey)}`;
  if (collection) return `/exploring/${repo}/${collection}`;
  return `/exploring/${repo}`;
}

const ATURI_BASE = 'https://aturi.to';

/**
 * Parse an `at://repo/collection/rkey` URI (or bare DID) into its parts.
 * Returns `null` for empty/unparseable input.
 */
function atUriParts(input) {
  if (!input) return null;
  const s = String(input);
  if (s.startsWith('did:')) return { repo: s, collection: null, rkey: null };
  const m = s.match(/^at:\/\/([^/]+)(?:\/([^/?#]+)(?:\/([^/?#]+))?)?/);
  if (!m) return null;
  return { repo: m[1], collection: m[2] || null, rkey: m[3] || null };
}

/**
 * Build the aturi.to universal-link page URL for a record — the friendly
 * "open this in the client of your choice" landing page. Mirrors aturi.to's
 * own canonical `/profile/…` form, including its `post` / `lists` aliases.
 *
 *   at://did/app.bsky.feed.post/xyz   → https://aturi.to/profile/did/post/xyz
 *   at://did/app.bsky.graph.list/xyz  → https://aturi.to/profile/did/lists/xyz
 *   at://did/is.dame.blog/xyz         → https://aturi.to/profile/did/is.dame.blog/xyz
 *   at://did                          → https://aturi.to/profile/did
 */
export function aturiUniversalUrl(input) {
  const parts = atUriParts(input);
  if (!parts) return null;
  const { repo, collection, rkey } = parts;
  if (collection && rkey) {
    const tail =
      collection === 'app.bsky.feed.post'
        ? `post/${encodeURIComponent(rkey)}`
        : collection === 'app.bsky.graph.list'
          ? `lists/${encodeURIComponent(rkey)}`
          : `${collection}/${encodeURIComponent(rkey)}`;
    return `${ATURI_BASE}/profile/${repo}/${tail}`;
  }
  return `${ATURI_BASE}/profile/${repo}`;
}

/**
 * Build the aturi.to Atmosphere Explorer URL for a record / collection /
 * repo — the raw-data inspector view.
 *
 *   at://did/app.bsky.feed.post/xyz  → https://aturi.to/explore/did/app.bsky.feed.post/xyz
 *   at://did/app.bsky.feed.post      → https://aturi.to/explore/did/app.bsky.feed.post
 *   at://did                         → https://aturi.to/explore/did
 */
export function aturiExplorerUrl(input) {
  const parts = atUriParts(input);
  if (!parts) return null;
  const { repo, collection, rkey } = parts;
  if (collection && rkey) return `${ATURI_BASE}/explore/${repo}/${collection}/${encodeURIComponent(rkey)}`;
  if (collection) return `${ATURI_BASE}/explore/${repo}/${collection}`;
  return `${ATURI_BASE}/explore/${repo}`;
}
